/**
 * Object Type Store - API methods (loadTypes, loadMappings, saveMappings)
 */

import { storage } from '../../lib/utils';
import { objectTypeApi, layerMappingApi } from '../../api';
import { log, LogCategory } from '../../lib/logger';
import { getStorageKey } from './persistence';
import { applyStrokeOnlyDefaults } from './strokeOnlyDefaults';
import { extractStyleFromApiProperties } from './stylePropertySync';
import type { ParsedEntity } from '../../../map-editor-features/csv/lib/csvParser';
import type { ObjectType, LayerTypeMapping, ObjectTypeState } from './types';

type Get = () => ObjectTypeState;
type Set = (partial: Partial<ObjectTypeState>) => void;

/**
 * Monotonic generations for API loaders. If the user switches maps/floors quickly, an older
 * in-flight response must not overwrite state for the current context.
 */
let loadTypesGeneration = 0;
let loadMappingsGeneration = 0;

export const createApiMethods = (set: Set, get: Get) => ({
  /**
   * Load object types from API
   */
  loadTypes: async () => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      log.warn(LogCategory.GENERAL, 'loadTypes: No project ID set');
      return;
    }

    const generation = ++loadTypesGeneration;
    const projectIdAtStart = currentProjectId;

    set({ isLoading: true, error: null });

    try {
      const response = await objectTypeApi.getList(currentProjectId);
      // response.data is directly the array (ListObjectTypesResponse = ObjectTypeResponse[])
      const apiTypes = response.data || [];

      log.info(LogCategory.GENERAL, 'Loaded object types from API', {
        projectId: currentProjectId,
        count: apiTypes.length,
      });

      // Convert API types (number IDs) to local types (string IDs).
      // 백엔드는 stroke/fill 스타일을 저장하지 않으므로, 조명 프레임 / 존 / 외곽선 같은
      // stroke-only 타입은 이름 기준으로 "채우기 없음 + 10px" 기본값을 파생시킨다.
      // 이렇게 해야 템플릿 import 직후뿐 아니라 재진입(loadTypes) 후에도 좌측 패널에
      // 동일하게 고정되어 보인다.
      // properties JSON 의 '__style' 예약 항목에서 확장 필드(kind/fontSize/
      // labelWidth/stroke·fill 스타일)를 복원한다. (stylePropertySync 참고)
      const types: ObjectType[] = apiTypes.map((apiType) => {
        const { style, properties } = extractStyleFromApiProperties(apiType.properties as any);
        return applyStrokeOnlyDefaults({
          id: apiType.id.toString(),
          name: apiType.name,
          icon: apiType.icon,
          color: apiType.color,
          priority: apiType.priority,
          properties,
          created: apiType.created_at,
          modified: apiType.updated_at,
          ...style,
        });
      });

      if (generation !== loadTypesGeneration || get().currentProjectId !== projectIdAtStart) {
        if (generation === loadTypesGeneration) {
          set({ isLoading: false });
        }
        log.info(LogCategory.GENERAL, 'loadTypes: stale response ignored', {
          generation,
          projectIdAtStart,
        });
        return;
      }

      // 방어 가드: API 가 빈 배열을 반환했는데 local store(또는 localStorage) 에 이미
      // ObjectType 들이 있으면 덮어쓰지 않는다. 현재 addType/addTypes 는 localStorage 에만
      // 저장하고 API 로 push 하지 않으므로 (template 시드 포함), API 응답 0건은 단순히
      // "서버는 모르는 상태" 일 뿐 실제 삭제 의도가 아니다. 덮어쓰면 사용자 입장에선
      // 대시보드 → 에디터 재진입 후 ObjectType 이 사라진 것처럼 보인다.
      const state = get();
      const localTypeCount = state.types.length;
      if (apiTypes.length === 0 && localTypeCount > 0) {
        log.warn(LogCategory.GENERAL, 'loadTypes: API returned empty but local has types — preserving local', {
          projectId: currentProjectId,
          localCount: localTypeCount,
        });
        set({ isLoading: false });
        return;
      }

      // Persist to localStorage so setCurrentLot can reload correctly
      const persistState = {
        types,
        mappings: state.mappings,
        layerTypeMappings: state.layerTypeMappings,
      };
      storage.set(getStorageKey(state.currentLotId), persistState);

      set({ types, isLoading: false });
    } catch (error) {
      if (generation !== loadTypesGeneration || get().currentProjectId !== projectIdAtStart) {
        if (generation === loadTypesGeneration) {
          set({ isLoading: false });
        }
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to load object types';
      log.error(LogCategory.GENERAL, 'loadTypes failed', { error: errorMessage });
      set({ error: errorMessage, isLoading: false });
    }
  },

  /**
   * Load layer type mappings for a specific floor from API (Optimized)
   * 레이어 단위 매핑만 로드 - 엔티티 로드 시 자동 적용됨
   *
   * CRITICAL FIX: Prevents race condition by clearing old floor's mappings immediately
   * and blocking rendering until new mappings are loaded
   */
  loadMappings: async (floorId: string) => {
    const { currentProjectId } = get();
    if (!currentProjectId) {
      log.warn(LogCategory.GENERAL, 'loadMappings: No project ID set');
      return;
    }

    const generation = ++loadMappingsGeneration;
    const projectIdAtStart = currentProjectId;

    // CRITICAL: Immediately clear this floor's old mappings to prevent race condition
    // Rendering will be blocked until mappingsLoading = false
    const state = get();
    const otherFloorLayerMappings = state.layerTypeMappings.filter((m) => String(m.floorId) !== String(floorId));

    set({
      mappingsLoading: true,
      currentLoadingFloorId: floorId,
      layerTypeMappings: otherFloorLayerMappings, // Clear current floor's old mappings
      error: null
    });

    log.info(LogCategory.GENERAL, '🔄 Loading layer mappings - rendering blocked', {
      floorId,
      clearedPreviousMappings: state.layerTypeMappings.length - otherFloorLayerMappings.length
    });

    try {
      const response = await layerMappingApi.getList(currentProjectId, floorId);
      const apiMappings = response.data || [];

      log.info(LogCategory.GENERAL, 'Loaded layer type mappings from API', {
        projectId: currentProjectId,
        floorId,
        count: (apiMappings as any).length,
      });

      // Convert API layer type mappings to local format
      const loadedLayerMappings: LayerTypeMapping[] = (apiMappings as any).map((apiMapping: any) => ({
        id: apiMapping.id.toString(),
        floorId: apiMapping.floor_id,
        layerName: apiMapping.layer_name,
        typeId: apiMapping.object_type_id.toString(),
        entityCount: apiMapping.entity_count,
        created: apiMapping.created_at,
      }));

      if (generation !== loadMappingsGeneration || get().currentProjectId !== projectIdAtStart) {
        if (generation === loadMappingsGeneration) {
          set({ mappingsLoading: false, currentLoadingFloorId: null });
        }
        log.info(LogCategory.GENERAL, 'loadMappings: stale response ignored', {
          floorId,
          generation,
          projectIdAtStart,
        });
        return;
      }

      // Merge with other floors' mappings
      const layerTypeMappings = [...otherFloorLayerMappings, ...loadedLayerMappings];

      // CRITICAL: Set mappingsLoading = false to unblock rendering
      set({
        layerTypeMappings,
        mappingsLoading: false,
        currentLoadingFloorId: null
      });

      log.info(LogCategory.GENERAL, '✅ Layer type mappings loaded - rendering unblocked', {
        floorId,
        layerCount: loadedLayerMappings.length,
        layers: loadedLayerMappings.map(m => m.layerName),
      });
    } catch (error) {
      if (generation !== loadMappingsGeneration || get().currentProjectId !== projectIdAtStart) {
        if (generation === loadMappingsGeneration) {
          set({ mappingsLoading: false, currentLoadingFloorId: null });
        }
        return;
      }
      const errorMessage = error instanceof Error ? error.message : 'Failed to load layer type mappings';
      log.error(LogCategory.GENERAL, 'loadMappings failed', { error: errorMessage, floorId });
      // CRITICAL: Unblock rendering even on error (with empty mappings)
      set({
        error: errorMessage,
        mappingsLoading: false,
        currentLoadingFloorId: null
      });
    }
  },

  /**
   * Save layer type mappings for a specific floor to API (Optimized)
   * 레이어 단위 매핑으로 99.8% DB row 감소
   * @param floorId - Floor ID
   * @param entities - CSV parsed entities for the current floor
   */
  saveMappings: async (floorId: string, entities: ParsedEntity[]) => {
    const { currentProjectId, mappings } = get();
    if (!currentProjectId) {
      throw new Error('No project ID set. Call setCurrentProjectId() first.');
    }

    // Use String() for type-safe comparison
    const floorMappings = mappings.filter((m) => String(m.floorId) === String(floorId));

    if (floorMappings.length === 0) {
      log.warn(LogCategory.GENERAL, 'saveMappings: No mappings to save', { floorId });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Build entity lookup map for quick access
      const entityMap = new Map<string, ParsedEntity>();
      entities.forEach((entity) => {
        entityMap.set(entity.entityHandle, entity);
      });

      // Group mappings by layer name (Optimization: 3000 entities → 5 layers)
      const layerToTypeMap = new Map<string, number>();

      floorMappings.forEach((mapping) => {
        const entity = entityMap.get(mapping.entityHandle);
        if (entity) {
          // 각 레이어마다 하나의 object_type_id만 저장
          layerToTypeMap.set(entity.layer, parseInt(mapping.typeId, 10));
        }
      });

      // Convert to API format (layer-level mappings only)
      const mappingItems = Array.from(layerToTypeMap.entries()).map(([layerName, objectTypeId]) => ({
        layer_name: layerName,
        object_type_id: objectTypeId,
      }));

      log.info(LogCategory.GENERAL, 'Saving layer type mappings to API (Optimized)', {
        projectId: currentProjectId,
        floorId,
        layerCount: mappingItems.length,
        originalEntityCount: floorMappings.length,
        reductionRate: `${((1 - mappingItems.length / floorMappings.length) * 100).toFixed(1)}%`,
      });

      const response = await layerMappingApi.batchCreate(
        currentProjectId,
        floorId,
        { mappings: mappingItems }
      );

      log.info(LogCategory.GENERAL, 'Layer type mappings saved successfully', {
        projectId: currentProjectId,
        floorId,
        createdCount: response.data?.created_count,
      });

      set({ isLoading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save mappings';
      log.error(LogCategory.GENERAL, 'saveMappings failed', {
        error: errorMessage,
        floorId,
        projectId: currentProjectId,
      });
      set({ error: errorMessage, isLoading: false });
      throw error; // Re-throw to allow caller to handle
    }
  },
});
